import React, {
    createContext,
    FC,
    ForwardedRef,
    ReactNode,
    Ref,
    useCallback,
    useContext,
    useEffect,
    useState,
    useMemo,
    useRef,
    CSSProperties,
} from 'react';
import { useLabel, useRadio, useRadioGroup, VisuallyHidden } from 'react-aria';
import { RadioGroupState, useRadioGroupState } from 'react-stately';
import { Loggable, useLogger } from '@direct-frontend/modules-logger';
import { castRef, genericForwardRef, genericMemo, mergeRefs, useIsomorphicLayoutEffect } from '@direct-frontend/stdlib';

import { cn } from '../../lib/css/className/className';
import { Button } from '../Button/Button';
import { useHStack } from '../../hooks/useStack/useStack';
import './MultiButton.css';
import { TestProps } from '../../internal/types/testTypes';
import { IconArrowDown, IconArrowUp, IconSelectSkinny } from '../../icons';
import { List, ListItem, ListSection } from '../List';
import { MenuTrigger, MenuTriggerProps } from '../MenuTrigger';
import { ClickableIcon } from '../ClickableIcon';
import { VStack } from '../Stack';
import { useFocusOptions } from '../../hooks/useFocusOptions';
import { SkinColor } from '../../lib/skin/getColorSkin/generated/types';
import { getColorSkin } from '../../lib/skin/getColorSkin/getColorSkin';

const cls = cn('MultiButton');

export type MultiButtonColor = 'gray' | 'white';

export type MultiButtonSize = 'xs' | 's' | 'm';

export type MultiButtonOption<T extends string = string> = {
    /**
     * Значение радиокнопки
     */
    value: T;

    /**
     * Контент радиокнопки (поясняющий текст или иконка)
     */
    content?: ReactNode;

    /**
     * Дополнительный текст радиокнопки
     */
    caption?: string;

    /**
     * Иконка в качестве единственного содержимого радиокнопки
     */
    icon?: ReactNode;

    /**
     * Текст во всплывающей подсказке при наведении на элемент
     */
    title?: string;

    /**
     * Признак отключения элемента
     */
    disabled?: boolean;
};

/**
 * Секция с дополнительными опциями для выпадающего списка
 */
export type MultiButtonExtraOptionsSection<T extends string, TMultiButtonOption> = {
    /**
     * Уникальный ключ секции
     */
    key: T;
    /**
     * Опциональный аголовок секции
     */
    title?: string;
    /**
     * Элементы секции
     */
    items: TMultiButtonOption[];
};

export interface MultiButtonProps<T extends string = string> extends TestProps, Loggable {
    /**
     * Набор опций
     */
    options: MultiButtonOption<T>[];

    /**
     * Набор дополнительных опций, скрытых в выпадающий список
     */
    extraOptions?: Array<MultiButtonExtraOptionsSection<string, MultiButtonOption<T>>>;

    /**
     * Размер кнопки
     * @default m
     */
    size?: MultiButtonSize;

    /**
     * Названия классов для элемента
     */
    className?: string;

    /**
     * Цветовая палитра для элемента
     * Перечисленные цвета определены дизайн-системой
     * @default gray
     */
    color?: MultiButtonColor;

    /**
     * Событие, вызываемое во время изменения радиогруппы
     */
    onChange?: (value: T) => void;

    /**
     * Значение группы
     */
    value?: T;

    /**
     * Значение группы по умолчанию
     */
    defaultValue?: T;

    /**
     * Название группы
     */
    name?: string;

    /**
     * Переключение режима отображение кнопок в группе.
     * При включении параметра все кнопки в группе имеют одинаковый размер.
     * @default true
     */
    withEqualButtons?: boolean;

    /**
     * Подпись к элементу для screen-readers
     */
    'aria-label'?: string;

    /**
     * Ширина контейнера MultiButton.
     * auto — по контенту всех опций, значение по умолчанию.
     * max — на всю ширину контейнера.
     * @default auto
     */
    width?: 'auto' | 'max';
}

interface RadioButtonProps extends TestProps, Pick<MultiButtonProps, 'size'> {
    /**
     * Опция
     */
    option: MultiButtonOption;

    /**
     * Значение группы
     */
    value?: string;

    /**
     * Ссылка на активный элемент (для корректной работы анимации переключения)
     */
    checkedRadioButtonRef: Ref<HTMLButtonElement>;

    /**
     * Опциональная иконка справа в кнопке
     */
    iconRight?: ReactNode;

    /**
     * Опциональный обработчик для клика по элементу
     */
    onClick?: () => void;
}

const skinMap: Record<MultiButtonColor, SkinColor> = {
    gray: 'gray-low-contrast',
    white: 'gray-neutral',
};

const sliderSkinMap: Record<MultiButtonColor, SkinColor> = {
    gray: 'gray-neutral',
    white: 'gray-low-contrast',
};

// Исправить при редактировании. Автоматический игнор, см. DIRECT-193196.
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- см. выше
const RadioContext = createContext({} as RadioGroupState);

const RadioButton: FC<RadioButtonProps> = function RadioButton({
    size = 'm',
    option,
    value,
    checkedRadioButtonRef,
    'data-testid': dataTestId = 'MultiButton',
    iconRight,
    onClick,
}) {
    // подготовка параметров для input
    const isActiveOption = option.value === value;

    const state = useContext(RadioContext);
    const ref = useRef(null);

    const accessibilityLabel = (typeof option.content === 'string' && option.content) || option.title || option.value;

    const { labelProps, fieldProps } = useLabel({
        label: accessibilityLabel,
        'aria-label': accessibilityLabel,
    });

    const { inputProps } = useRadio(
        {
            value: option.value,
            isDisabled: option.disabled,
            ...fieldProps,
        },
        state,
        ref,
    );

    const radioInput = useMemo(
        () => (
            <VisuallyHidden>
                <input
                    ref={ref}
                    type="radio"
                    autoComplete="off"
                    aria-autocomplete="none"
                    className={cls('input')}
                    {...inputProps}
                />
            </VisuallyHidden>
        ),
        [inputProps, ref],
    );

    // обертка используется для корректного выравнивания иконки внутри кнопки и одновременного использования input
    const icon = useMemo(
        () => (option.icon ? () => <div className={cls('icon')}>{option.icon}</div> : undefined),
        [option.icon],
    );

    const onButtonClick = useCallback(() => {
        state.setSelectedValue(option.value);
        onClick?.();
    }, [onClick, option.value, state]);

    const buttonIconRight = useMemo(() => (iconRight ? () => <>{iconRight}</> : undefined), [iconRight]);

    // для соответствия области клика - кнопка реализована как label
    // TODO DIRECT-243913 Вернуть label и откатить изменения после фикса
    // TODO DIRECT-251974 Привести размеры согласно дизайну
    return (
        <Button
            data-testid={`${dataTestId}${isActiveOption ? '.selected' : ''}`}
            id={labelProps.id}
            ref={isActiveOption ? checkedRadioButtonRef : undefined}
            as="div"
            className={cls('button', { size, selected: isActiveOption })}
            size={size === 'm' ? 's' : size}
            color="text"
            isDisabled={option.disabled}
            title={option.title}
            icon={icon}
            onPress={onButtonClick}
            logNode={`${option.value}.Button`}
            caption={option.caption}
            iconRight={buttonIconRight}
        >
            {radioInput}
            {option.content}
        </Button>
    );
};

/**
 * Компонент для отображения группы из кнопок
 */
export const MultiButton = genericMemo(
    genericForwardRef(function MultiButton<TValue extends string = string>(
        {
            className,
            color = 'gray',
            options,
            extraOptions,
            onChange,
            value,
            defaultValue,
            name,
            withEqualButtons = true,
            width = 'auto',
            'data-testid': dataTestId = 'MultiButton',
            logNode,
            size = 'm',
            ...props
        }: MultiButtonProps<TValue>,
        forwardedRef: ForwardedRef<HTMLDivElement>,
    ) {
        // TODO DIRECT-263743 Поресерчить фокусы в модалке на мобиле, после этого если нужно - откатить
        const { focusProps, isFocusVisible } = useFocusOptions({ within: true });
        const [sliderProps, setSliderProps] = useState<CSSProperties | undefined>();
        const { LogProvider, logClick } = useLogger('MultiButton', logNode);
        const checkedRadioButtonRef = useRef<HTMLButtonElement>(null);
        const containerRef = useRef<HTMLDivElement>(null);
        const mergedContainerRef = useMemo(() => mergeRefs(containerRef, forwardedRef), [forwardedRef]);
        const [isResizing, setIsResizing] = useState(false);
        const transitionTimeout = useRef<ReturnType<typeof setTimeout>>();

        const isButtonOptionSelected = useMemo(() => options.some(option => option.value === value), [options, value]);
        const extraOptionSelected = useMemo(
            () =>
                // При выборе из двух дубликатов значений даем приоритет обычной кнопке.
                // Это позволит сделать разный текст кнопки в зависимости от места отображения:
                // в кнопках - короткий текст, а в меню - длинный текст,
                // но не дублировать кнопки (7 дней, последние 7 дней)
                !isButtonOptionSelected &&
                extraOptions
                    ?.map(section => section.items)
                    .flat()
                    .find(option => option.value === value),
            [extraOptions, isButtonOptionSelected, value],
        );
        const isExtraOptionSelected = Boolean(extraOptionSelected);

        // перемещение бегунка (показывает активный элемент)
        const updateSliderSizeAndPosition = useCallback(() => {
            if (!checkedRadioButtonRef.current) {
                return;
            }

            const transform = `translate(${checkedRadioButtonRef.current.offsetLeft}px)`;
            const sliderWidth = `${checkedRadioButtonRef.current.offsetWidth}px`;

            setSliderProps(current =>
                current && current.transform === transform && current.width === sliderWidth
                    ? current
                    : { transform, width: sliderWidth },
            );
        }, []);

        // отключение анимации при ресайзе
        const handleResize = useCallback(() => {
            setIsResizing(true);
            clearTimeout(transitionTimeout.current);
            transitionTimeout.current = setTimeout(() => {
                setIsResizing(false);
            }, 50);
        }, []);

        const observer = useMemo(() => {
            if (typeof window === 'undefined' || !window.ResizeObserver) {
                return;
            }

            return new ResizeObserver(() => {
                updateSliderSizeAndPosition();
                requestAnimationFrame(handleResize);
            });
        }, [updateSliderSizeAndPosition, handleResize]);

        useEffect(() => {
            // отслеживание размера контейнера требуется для предотвращения возможных визуальных багов
            if (containerRef.current) {
                observer?.observe(containerRef.current);
            }
            return () => {
                observer?.disconnect();
                clearTimeout(transitionTimeout.current);
            };
        }, [observer]);

        // Зависимости эффекта — примитивы: options и extraOptions родители обычно
        // пересоздают на каждом рендере, и зависимость от их identity заставляла бы
        // эффект читать offsetLeft/offsetWidth (forced reflow) на каждом ререндере
        // страницы. Изменения геометрии без смены значения ловит ResizeObserver выше.
        const optionsKey = options.map(option => option.value).join('\u001F');
        const extraOptionSelectedValue = extraOptionSelected ? extraOptionSelected.value : undefined;

        /**
         * Используем именно useLayoutEffect вместо useEffect,
         * чтобы переключение из состояния выбранной опции из extraOption
         * к состоянию выбранной опции из options выглядело лучше.
         */
        useIsomorphicLayoutEffect(() => {
            updateSliderSizeAndPosition();
        }, [value, optionsKey, extraOptionSelectedValue, updateSliderSizeAndPosition]);

        // подготовка кнопок
        const buttons = options.map((option, index) => {
            return (
                <RadioButton
                    size={size}
                    data-testid={`${dataTestId}.${option.value}`}
                    option={option}
                    value={value}
                    checkedRadioButtonRef={checkedRadioButtonRef}
                    key={index}
                />
            );
        });

        const handleChange = useCallback(
            (value: string) => {
                logClick({ type: 'onMultiButtonChange', value });

                // react-stately возвращает string, восстанавливаем типизированное значение через поиск по опциям
                const typedOption =
                    options.find(o => o.value === value) ??
                    extraOptions?.flatMap(s => s.items).find(o => o.value === value);

                if (typedOption) {
                    onChange?.(typedOption.value);
                }
            },
            [onChange, logClick, options, extraOptions],
        );

        // контроль состояния для радиогруппы
        const state = useRadioGroupState({
            value: value ?? '',
            defaultValue: defaultValue,
            onChange: handleChange,
        });

        const { radioGroupProps } = useRadioGroup(
            { orientation: 'horizontal', name: name, 'aria-label': props['aria-label'] },
            state,
        );

        const stackClasses = useHStack({
            justifyContent: 'center',
            alignItems: 'center',
        });

        const sliderSkinClass = getColorSkin(sliderSkinMap[color], {});

        const slider =
            sliderProps && (isButtonOptionSelected || isExtraOptionSelected) ? (
                <span
                    className={cls(
                        'slider',
                        {
                            size,
                            'no-transition': isResizing && !isExtraOptionSelected,
                        },
                        [sliderSkinClass],
                    )}
                    style={sliderProps}
                />
            ) : undefined;

        const renderExtraOptionsTrigger = useCallback<MenuTriggerProps['trigger']>(
            (triggerProps, { isOpen }) => {
                const ref = castRef(triggerProps?.ref, 'button');
                const onClick = triggerProps.onClick;

                if (extraOptionSelected) {
                    const arrowIcon = isOpen ? (
                        <IconArrowUp className={cls('trigger-icon')} role="supplementary" />
                    ) : (
                        <IconArrowDown className={cls('trigger-icon')} role="supplementary" />
                    );
                    return (
                        <RadioButton
                            data-testid={`${dataTestId}.${extraOptionSelected.value}`}
                            option={extraOptionSelected}
                            value={value}
                            size={size}
                            onClick={onClick}
                            checkedRadioButtonRef={ref ? mergeRefs(ref, checkedRadioButtonRef) : checkedRadioButtonRef}
                            key={options.length + 1}
                            iconRight={
                                <VStack
                                    justifyContent="center"
                                    data-testid={`${dataTestId}.ExtraOptionsTrigger${isOpen ? '.open' : ''}`}
                                >
                                    {arrowIcon}
                                </VStack>
                            }
                        />
                    );
                }
                return (
                    <ClickableIcon
                        {...triggerProps}
                        data-testid={`${dataTestId}.ExtraOptionsTrigger${isOpen ? '.open' : ''}`}
                        className={cls('trigger', { size })}
                        color="current"
                        ref={ref}
                    >
                        {isOpen ? <IconArrowUp /> : <IconArrowDown />}
                    </ClickableIcon>
                );
            },
            [dataTestId, extraOptionSelected, options.length, size, value],
        );

        const extraOptionsButton = useMemo(() => {
            if (!extraOptions) {
                return;
            }

            return (
                <MenuTrigger
                    key={`${options.length + 1}.Extra`}
                    data-testid={`${dataTestId}.ExtraButtonsExpander`}
                    trigger={renderExtraOptionsTrigger}
                    placement={['bottom-start', 'bottom-end']}
                >
                    <List size={size}>
                        {extraOptions.map(({ key, title, items: sectionItems }) => (
                            <ListSection key={key} title={title}>
                                {sectionItems.map(sectionItem => (
                                    <ListItem<string>
                                        data-testid={`${dataTestId}.ExtraOptions.${sectionItem.value}`}
                                        id={sectionItem.value}
                                        // DIRECT-337096: элемент исключён из поиска,
                                        // при доработке заменить на текст для поиска
                                        textValue={null}
                                        key={`${key}_${sectionItem.value}`}
                                        onClick={handleChange}
                                        iconRight={value === sectionItem.value ? IconSelectSkinny : undefined}
                                    >
                                        {sectionItem.content}
                                    </ListItem>
                                ))}
                            </ListSection>
                        ))}
                    </List>
                </MenuTrigger>
            );
        }, [dataTestId, extraOptions, handleChange, options.length, renderExtraOptionsTrigger, size, value]);

        if (extraOptionsButton) {
            buttons.push(extraOptionsButton);
        }

        const skinClass = getColorSkin(skinMap[color], {});

        return (
            <LogProvider>
                <div
                    data-testid={dataTestId}
                    ref={mergedContainerRef}
                    className={cls({ size, withEqualButtons, focused: isFocusVisible, width }, [
                        className,
                        stackClasses,
                        skinClass,
                    ])}
                    {...radioGroupProps}
                    {...focusProps}
                >
                    {slider}
                    <RadioContext.Provider value={state}>{buttons}</RadioContext.Provider>
                </div>
            </LogProvider>
        );
    }),
);
